import { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Button,
  Icons,
  Skeleton,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import {
  WorkloadDetailsContext,
  makeEditScalingPolicySettingsLink,
} from '@cast/split-me/features/cluster/workload-optimization';
import type { PreparedWorkload } from '@cast/split-me/features/workload-optimization';

import {
  ScalingPolicyAutocomplete,
  useInventoryWorkloadScalingPolicy,
} from '../_hooks/useInventoryWorkloadScalingPolicy';

type ScalingPolicyContentProps = {
  workload: PreparedWorkload;
};

const ScalingPolicyContent = ({ workload }: ScalingPolicyContentProps) => {
  const {
    policies,
    isAutoAssignmentEnabled,
    isDisabled,
    isNamespaceWritableForWorkload,
    onChange,
    onHeaderClick,
  } = useInventoryWorkloadScalingPolicy({ workload });

  const policyId = workload.verticalScalingPolicy?.id;
  const policyLink = policyId
    ? makeEditScalingPolicySettingsLink({ policyId })
    : undefined;

  return (
    <Stack gap={4} width="100%">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography variant="P12M" color={tokens.theme.fg.structural.secondary}>
          {isAutoAssignmentEnabled
            ? 'Auto-Assigned Scaling Policy'
            : 'Scaling Policy'}
        </Typography>
        {policyLink && (
          <Button
            variant="text"
            endIcon={<Icons.ArrowRight />}
            component={RouterLink}
            to={policyLink}
            testId="workload-policy-go-to-policy"
          >
            Go to policy
          </Button>
        )}
      </Stack>
      <ScalingPolicyAutocomplete
        testId="workload-policy-select"
        workload={workload}
        policies={policies}
        disabled={isDisabled}
        onChange={onChange}
        showSystemPolicyLabel
        isNamespaceWritableForWorkload={isNamespaceWritableForWorkload}
        onHeaderClick={onHeaderClick}
        showOptimizationTypeChips
        size="md"
        showEndAdornment={false}
      />
    </Stack>
  );
};

export const InventoryWorkloadScalingPolicySelect = () => {
  const { workload } = useContext(WorkloadDetailsContext);

  if (!workload) {
    return (
      <Stack gap={4} width="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography
            variant="P12M"
            color={tokens.theme.fg.structural.secondary}
          >
            Scaling Policy
          </Typography>
        </Stack>
        <Skeleton height={40} width="100%" />
      </Stack>
    );
  }

  return <ScalingPolicyContent workload={workload} />;
};
